import { useEffect, useState, useMemo, useRef, lazy, Suspense } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { LayoutHeader as Header } from '@/layouts';
import { Footer } from '@/components/Footer';
import { VehicleSection } from '@/components/VehicleSection';
import { LayoutVehicleCard as VehicleCard } from '@/layouts';
import { extractIdFromSlug, generateVehicleSlug } from '@/lib/slug';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { secureGetItem, secureSetItem } from '@/lib/storage';
import { openWhatsApp } from '@/lib/whatsapp';
import SEOHead from '@/components/SEOHead';
import { generateVehicleStructuredData, generateBreadcrumbStructuredData } from '@/lib/structuredData';
import { useAppData } from '@/contexts/AppDataContext';
import { useStore } from '@/contexts/StoreContext';
import {
  MessageCircle,
  Heart,
  Share2,
  Calendar,
  Gauge,
  Fuel,
  Settings,
  Palette,
  Car,
  Shield,
  Phone,
  MapPin,
  Building2,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Eye,
  Star,
  CreditCard,
  Maximize,
  X,
  Send,
  File,
  FilePen,
  Navigation
} from 'lucide-react';

import { useTheme } from '@/contexts/ThemeContext';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import { requestUserLocation, getDistanceToStore, formatDistance } from '@/lib/geolocation';
import { generateResponsiveImageUrls, getImageLoadingStrategy } from '@/lib/imageOptimization';

// Import service images
import financing from '@/assets/services/financing.png';
import testdrive from '@/assets/services/testdrive.png';
import { VehicleNotFound } from '@/components/VehicleNotFound';
import { MobileImageViewer } from '@/components/MobileImageViewer';

// Lazy load ServiceModal to reduce initial bundle size
const ServiceModal = lazy(() => import('@/components/ServiceModal').then(m => ({ default: m.ServiceModal })));
import { trackVehicleView, trackLeadSubmission, trackWhatsAppClick, trackAddToFavorites } from '@/lib/analytics';
import { openStoreDirections } from '@/lib/storeMapUrls';

// Lead Form Component (for API type and message type)
const LeadForm = ({ vehicle, config, buttonColor, store, serviceType = 'message', serviceTitle = 'Envie uma mensagem', service }: { vehicle: any; config: any; buttonColor: string; store: any; serviceType?: string; serviceTitle?: string; service?: any }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  // Pre-fill message with vehicle info on mount
  useEffect(() => {
    let vehicleInfo = '';

    if (serviceType === '65dcb7898eca108d87c91dbc') {
      // Financing service
      vehicleInfo = `Olá,\n\nGostaria de simular um financiamento para o veículo:\n\n${vehicle.brand} ${vehicle.model} ${vehicle.version} ${vehicle.modelYear}\nPreço: ${formatPrice(vehicle.price)}\n\nPor favor, entrem em contato para mais informações sobre as condições de financiamento.`;
    } else {
      // Default message service
      vehicleInfo = `Olá,\n\nTenho interesse no veículo:\n\n${vehicle.brand} ${vehicle.model} ${vehicle.version} ${vehicle.modelYear}\nPreço: ${formatPrice(vehicle.price)}\n\nGostaria de receber mais informações.`;
    }

    setFormData(prev => ({
      ...prev,
      message: vehicleInfo
    }));
  }, [vehicle, serviceType]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
    } else {
      return numbers.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
    }
  };

  const handlePhoneChange = (value: string) => {
    const formatted = formatPhoneNumber(value);
    handleInputChange('phone', formatted);
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast({
        title: '⚠️ Campo obrigatório',
        description: 'Por favor, preencha o campo Nome.',
        variant: 'destructive'
      });
      return false;
    }

    if (!formData.email.trim()) {
      toast({
        title: '⚠️ Campo obrigatório',
        description: 'Por favor, preencha o campo E-mail.',
        variant: 'destructive'
      });
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: '❌ E-mail inválido',
        description: 'Por favor, insira um endereço de e-mail válido.',
        variant: 'destructive'
      });
      return false;
    }

    if (!formData.phone.trim()) {
      toast({
        title: '⚠️ Campo obrigatório',
        description: 'Por favor, preencha o campo Telefone.',
        variant: 'destructive'
      });
      return false;
    }

    const phoneNumbers = formData.phone.replace(/\D/g, '');
    if (phoneNumbers.length < 10) {
      toast({
        title: '❌ Telefone inválido',
        description: 'Por favor, insira um número de telefone válido com DDD.',
        variant: 'destructive'
      });
      return false;
    }

    if (!formData.message.trim()) {
      toast({
        title: '⚠️ Campo obrigatório',
        description: 'Por favor, preencha o campo Mensagem.',
        variant: 'destructive'
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // Check if service has API type
      const isApiType = (service?.lead?.type === 'api' || service?.lead?.type === 'email');

      if (isApiType) {
        // Send via API
        const { LEAD } = await import('@/lib/api');

        // Prepare lead data following the correct format
        const leadData = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone.replace(/\D/g, ''), // Remove formatting
          message: formData.message,
          vehicle: `${vehicle.brand} ${vehicle.model} ${vehicle.version ? `${vehicle.version} ` : ' '}${vehicle.manufactureYear}/${vehicle.modelYear}`,
          id: vehicle.id,
          plaque: vehicle.plaque || '',
          brand: vehicle.brand,
          model: vehicle.model,
          version: vehicle.version || '',
          year: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
          price: formatPrice(vehicle.price),
          mileage: `${vehicle.mileage}km`
        };

        const response = await LEAD(serviceType, store, leadData);

        if (response?.status == 200) {
          // Track lead submission
          trackLeadSubmission({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            message: formData.message,
            serviceType,
            vehicle: {
              id: vehicle.id,
              brand: vehicle.brand,
              model: vehicle.model,
              version: vehicle.version || '',
              year: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
              price: vehicle.price,
              mileage: vehicle.mileage
            }
          });

          toast({
            title: 'Mensagem enviada!',
            description: 'Entraremos em contato em breve.',
          });

          // Reset form
          setFormData({
            name: '',
            email: '',
            phone: '',
            message: `Olá,\n\nTenho interesse no veículo:\n\n${vehicle.brand} ${vehicle.model} ${vehicle.version} ${vehicle.modelYear}\nPreço: ${formatPrice(vehicle.price)}\n\nGostaria de receber mais informações.`
          });
        } else {
          toast({
            title: 'Erro ao enviar',
            description: 'Tente novamente.',
            variant: 'destructive'
          });
        }
      } else {
        // Send via WhatsApp (type === 'message')
        toast({
          title: '✅ Redirecionando',
          description: 'Abrindo WhatsApp...',
        });

        const { openWhatsApp } = await import('@/lib/whatsapp');

        if (service?.lead?.ios) {
          // Build message from template (same logic as ServiceModal)
          let message = service.lead?.ios;
          const whatsappTemplate = message.substring(0, message.indexOf('&text=') + 6);
          message = message.substring(message.indexOf('&text=') + 6);

          // Prepare form data with vehicle info
          const messageData: Record<string, string> = {
            nome: formData.name,
            name: formData.name,
            email: formData.email,
            telefone: formData.phone,
            phone: formData.phone,
            mensagem: formData.message,
            message: formData.message,
            veiculo: `${vehicle.brand} ${vehicle.model} ${vehicle.version ? `${vehicle.version} ` : ''}${vehicle.manufactureYear}/${vehicle.modelYear}`,
            vehicle: `${vehicle.brand} ${vehicle.model} ${vehicle.version ? `${vehicle.version} ` : ''}${vehicle.manufactureYear}/${vehicle.modelYear}`,
            marca: vehicle.brand,
            brand: vehicle.brand,
            modelo: vehicle.model,
            model: vehicle.model,
            versao: vehicle.version || '',
            version: vehicle.version || '',
            ano: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
            year: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
            preco: formatPrice(vehicle.price),
            price: formatPrice(vehicle.price),
            km: `${vehicle.mileage}km`,
            mileage: `${vehicle.mileage}km`
          };

          // Replace placeholders with form data
          Object.entries(messageData).forEach(([key, value]) => {
            const regex = new RegExp(`\\{${key}\\}`, 'g');
            message = message.replace(regex, value);
          });

          // Add current date/time if needed
          const now = new Date();
          message = message.replace(/\{datahora\}/g, now.toLocaleString('pt-BR'));
          message = message.replace(/\{datetime\}/g, now.toLocaleString('pt-BR'));

          const whatsappUrl = whatsappTemplate + encodeURIComponent(message);
          openWhatsApp(whatsappUrl);
        } else {
          // Use default WhatsApp with message
          const phoneMatch = store.whatsapp?.ios?.match(/phone=([^&]+)/);
          const phone = phoneMatch ? phoneMatch[1] : '';
          openWhatsApp(`whatsapp://send?phone=${phone}&text=${encodeURIComponent(formData.message)}`);
        }

        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: `Olá,\n\nTenho interesse no veículo:\n\n${vehicle.brand} ${vehicle.model} ${vehicle.version} ${vehicle.modelYear}\nPreço: ${formatPrice(vehicle.price)}\n\nGostaria de receber mais informações.`
        });
      }
    } catch (error: any) {
      console.error('Error submitting lead:', error);

      toast({
        title: 'Erro ao enviar',
        description: error?.message || 'Ocorreu um erro ao enviar sua mensagem. Tente novamente.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-5">
      <CardTitle className="text-xl font-bold">{serviceTitle}</CardTitle>

      {/* Compact Form with Floating Labels */}
      <div className="space-y-4">
        {/* Name Field - Floating Label */}
        <div className="relative">
          <Input
            id="name"
            placeholder=" "
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className="h-12 peer pt-4 placeholder-transparent"
          />
          <Label
            htmlFor="name"
            className="absolute left-3 top-3 text-sm text-muted-foreground transition-all pointer-events-none peer-focus:top-1 peer-focus:text-xs peer-focus:text-primary peer-[:not(:placeholder-shown)]:top-1 peer-[:not(:placeholder-shown)]:text-xs"
          >
            Nome Completo <span className="text-red-500">*</span>
          </Label>
        </div>

        {/* Email Field - Floating Label */}
        <div className="relative">
          <Input
            id="email"
            type="email"
            placeholder=" "
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className="h-12 peer pt-4 placeholder-transparent"
          />
          <Label
            htmlFor="email"
            className="absolute left-3 top-3 text-sm text-muted-foreground transition-all pointer-events-none peer-focus:top-1 peer-focus:text-xs peer-focus:text-primary peer-[:not(:placeholder-shown)]:top-1 peer-[:not(:placeholder-shown)]:text-xs"
          >
            E-mail <span className="text-red-500">*</span>
          </Label>
        </div>

        {/* Phone Field - Floating Label */}
        <div className="relative">
          <Input
            id="phone"
            type="tel"
            placeholder=" "
            value={formData.phone}
            onChange={(e) => handlePhoneChange(e.target.value)}
            maxLength={15}
            className="h-12 peer pt-4 placeholder-transparent"
          />
          <Label
            htmlFor="phone"
            className="absolute left-3 top-3 text-sm text-muted-foreground transition-all pointer-events-none peer-focus:top-1 peer-focus:text-xs peer-focus:text-primary peer-[:not(:placeholder-shown)]:top-1 peer-[:not(:placeholder-shown)]:text-xs"
          >
            Telefone <span className="text-red-500">*</span>
          </Label>
        </div>

        {/* Message Field - Floating Label */}
        <div className="relative">
          <Textarea
            id="message"
            placeholder=" "
            value={formData.message}
            onChange={(e) => handleInputChange('message', e.target.value)}
            rows={4}
            className="resize-none peer pt-5 placeholder-transparent min-h-[100px]"
          />
          <Label
            htmlFor="message"
            className="absolute left-3 top-3 pt-1 right-3 h-6 text-sm bg-background text-muted-foreground transition-all pointer-events-none peer-focus:top-0 peer-focus:text-xs peer-focus:text-primary peer-[:not(:placeholder-shown)]:top-0 peer-[:not(:placeholder-shown)]:text-xs"
            style={{ marginTop: '1px' }}
          >
            Mensagem <span className="text-red-500">*</span>
          </Label>
        </div>

        {/* Submit Button - Clean Design */}
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="w-full h-11 text-white transition-all duration-200"
          style={{
            backgroundColor: buttonColor,
          }}
          onMouseEnter={(e) => {
            if (!isSubmitting) {
              e.currentTarget.style.opacity = '0.9';
            }
          }}
          onMouseLeave={(e) => {
            if (!isSubmitting) {
              e.currentTarget.style.opacity = '1';
            }
          }}
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
              Enviando...
            </>
          ) : (
            <>
              <Send className="h-4 w-4 mr-2" />
              Enviar Mensagem
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

const VehicleDetail = () => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { config, vehicles, isLoading } = useAppData();
  const { selectedStore, availableStores } = useStore();

  // Extract slug from params (can be :id or carro/*, moto/*, caminhao/*)
  const slugOrId = params.id || params['*'] || '';
  const [vehicle, setVehicle] = useState<any>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFavorited, setIsFavorited] = useState(false);
  const [relatedVehicles, setRelatedVehicles] = useState<any[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenImageIndex, setFullscreenImageIndex] = useState(0);
  const [isMobileViewerOpen, setIsMobileViewerOpen] = useState(false);
  const [mobileViewerIndex, setMobileViewerIndex] = useState(0);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [relatedSlugs, setRelatedSlugs] = useState<Record<number, string>>({});
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null);
  const [imagesPreloaded, setImagesPreloaded] = useState(false);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());
  const [failedImages, setFailedImages] = useState<Set<number>>(new Set());
  const [showRelatedArrows, setShowRelatedArrows] = useState(false);
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
  const [touchEnd, setTouchEnd] = useState<{ x: number; y: number } | null>(null);
  const relatedScrollRef = useRef<HTMLDivElement>(null);
  const { colors } = useTheme();
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const buttonColor = colors.button;
  const primaryColor = colors.primary;
  const secondaryColor = colors.secondary;
  const titleColor = colors.title;
  const textColor = colors.text;

  // Get the store for this vehicle (based on vehicle.store field)
  const vehicleStore = useMemo(() => {
    if (!vehicle) {
      return selectedStore || config.stores[0];
    }
    const storeId = vehicle.store;
    if (!storeId) {
      return selectedStore || config.stores[0];
    }
    return config.stores?.find((s: any) => s._id === storeId) || config.stores[0];
  }, [vehicle, config.stores, selectedStore]);

  // Request user location only when needed (user interaction)
  // Don't request automatically on page load to avoid trust issues

  // Preload all vehicle images when vehicle is loaded
  useEffect(() => {
    if (!vehicle || !vehicle.photos || vehicle.photos.length === 0) {
      setImagesPreloaded(false);
      return;
    }

    setImagesPreloaded(false);

    const preloadImages = async () => {
      // Preload first 3 and last 3 images (needed for infinite scroll)
      const totalPhotos = vehicle.photos.length;
      const criticalImages = [];

      // First 3 images (right side of carousel)
      const firstImages = vehicle.photos.slice(0, Math.min(3, totalPhotos));
      criticalImages.push(...firstImages);

      // Last 3 images (left side of carousel) - only if we have more than 3 photos
      if (totalPhotos > 3) {
        const lastImages = vehicle.photos.slice(-3);
        criticalImages.push(...lastImages);
      }

      // Preload critical images
      const criticalImagePromises = criticalImages.map((photoUrl: string) => {
        return new Promise<void>((resolve) => {
          const img = new Image();
          img.src = photoUrl;
          img.onload = () => resolve();
          img.onerror = () => resolve(); // Resolve anyway if error
        });
      });

      // Wait for critical images, then enable viewing
      await Promise.all(criticalImagePromises);
      setImagesPreloaded(true);

      // Continue loading remaining images in background
      if (totalPhotos > 6) {
        vehicle.photos.slice(3, -3).forEach((photoUrl: string) => {
          const img = new Image();
          img.src = photoUrl;
          // No need to wait for these
        });
      }
    };

    preloadImages();
  }, [vehicle]);

  // Reset image index when vehicle changes
  useEffect(() => {
    if (vehicle) {
      setCurrentImageIndex(0);
      setImagesPreloaded(false);
      setLoadedImages(new Set()); // Reset loaded images for LQIP
    }
  }, [vehicle?.id]);

  useEffect(() => {
    const loadVehicleData = async () => {
      // Only check for vehicle after data has loaded
      if (!isLoading && vehicles.length > 0) {
        // Extract ID from slug or use direct ID
        const vehicleId = await extractIdFromSlug(slugOrId || '') || slugOrId;
        const foundVehicle = vehicles.find(v => v.id.toString() === vehicleId);
        if (foundVehicle) {
          setVehicle(foundVehicle);

          // Track vehicle view
          trackVehicleView({
            id: foundVehicle.id,
            brand: foundVehicle.brand,
            model: foundVehicle.model,
            version: foundVehicle.version || '',
            year: `${foundVehicle.manufactureYear}/${foundVehicle.modelYear}`,
            price: foundVehicle.price,
            mileage: foundVehicle.mileage
          });

          // Reset to first image when vehicle changes
          setCurrentImageIndex(0);
          setImagesPreloaded(false);

          // Register vehicle as recently viewed
          try {
            const viewedStr = await secureGetItem('recentlyViewedVehicles');
            let viewedVehicles = viewedStr ? JSON.parse(viewedStr) : [];

            // Remove if already exists (to avoid duplicates)
            viewedVehicles = viewedVehicles.filter((v: any) => v.id !== foundVehicle.id);

            // Add to beginning of array with timestamp
            viewedVehicles.unshift({
              id: foundVehicle.id,
              timestamp: Date.now()
            });

            // Keep only last 5 vehicles to save localStorage space
            viewedVehicles = viewedVehicles.slice(0, 5);

            await secureSetItem('recentlyViewedVehicles', JSON.stringify(viewedVehicles));

            // Dispatch event to notify other components
            window.dispatchEvent(new Event('vehicleViewed'));
          } catch (error) {
            console.error('Erro ao registrar veículo visualizado:', error);
          }

          // Check if vehicle is favorited
          const favorites = await secureGetItem('favorites');
          if (favorites && Array.isArray(favorites)) {
            setIsFavorited(favorites.includes(foundVehicle.id));
          }

          // Determine if we should filter by store based on unifiedAds and unifiedAdsStore
          const unifiedAds = config?.unifiedAds !== false;
          const unifiedAdsStore = config?.unifiedAdsStore;
          const vehicleStoreId = foundVehicle.store;

          let filterByStore = false;

          if (!unifiedAds) {
            // Rule 1: unifiedAds is false → always filter by vehicle's store
            filterByStore = true;
          } else if (unifiedAdsStore !== null && unifiedAdsStore !== undefined) {
            // Rule 3 & 4: unifiedAds is true and unifiedAdsStore is set
            // Only show all vehicles if vehicle's store is the unifiedAdsStore
            filterByStore = vehicleStoreId !== unifiedAdsStore;
          }
          // Rule 2: unifiedAds is true and unifiedAdsStore is null → don't filter (filterByStore = false)

          // Find related vehicles with priority: 1) same model, 2) same brand, 3) similar price
          // Filter by store if needed based on unifiedAds rules
          const sameModel = vehicles.filter(v =>
            v.id !== foundVehicle.id &&
            v.model === foundVehicle.model &&
            (!filterByStore || v.store === vehicleStoreId)
          );

          const sameBrand = vehicles.filter(v =>
            v.id !== foundVehicle.id &&
            v.brand === foundVehicle.brand &&
            v.model !== foundVehicle.model &&
            (!filterByStore || v.store === vehicleStoreId)
          );

          // Others: similar price range (±10,000)
          const priceMin = foundVehicle.price - 10000;
          const priceMax = foundVehicle.price + 10000;
          const others = vehicles.filter(v =>
            v.id !== foundVehicle.id &&
            v.brand !== foundVehicle.brand &&
            v.price >= priceMin &&
            v.price <= priceMax &&
            (!filterByStore || v.store === vehicleStoreId)
          );

          // Show only the highest priority available (don't mix)
          let related: any[] = [];
          if (sameModel.length > 0) {
            // Priority 1: Show only same model
            related = sameModel.slice(0, 4);
          } else if (sameBrand.length > 0) {
            // Priority 2: Show only same brand
            related = sameBrand.slice(0, 4);
          } else {
            // Priority 3: Show only similar price
            related = others.slice(0, 4);
          }

          setRelatedVehicles(related);
        }
        setHasLoaded(true);
      }
    };

    loadVehicleData();
  }, [slugOrId, vehicles, isLoading]);

  // Load slugs for related vehicles
  useEffect(() => {
    const loadRelatedSlugs = async () => {
      const newSlugs: Record<number, string> = {};
      for (const relatedVehicle of relatedVehicles) {
        newSlugs[relatedVehicle.id] = await generateVehicleSlug(relatedVehicle);
      }
      setRelatedSlugs(newSlugs);
    };
    if (relatedVehicles.length > 0) {
      loadRelatedSlugs();
    }
  }, [relatedVehicles]);

  // Handle keyboard navigation in fullscreen
  useEffect(() => {
    if (!vehicle) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isFullscreen) return;

      if (e.key === 'Escape') {
        setIsFullscreen(false);
      } else if (e.key === 'ArrowRight') {
        nextFullscreenImage();
      } else if (e.key === 'ArrowLeft') {
        prevFullscreenImage();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen, vehicle]);

  // Sync fullscreen index with main gallery
  useEffect(() => {
    if (isFullscreen) {
      setFullscreenImageIndex(currentImageIndex);
    }
  }, [currentImageIndex, isFullscreen]);

  // Check if arrows should be shown for related vehicles
  useEffect(() => {
    const checkScrollNeeded = () => {
      // Use requestAnimationFrame to avoid forced reflow
      requestAnimationFrame(() => {
        if (relatedScrollRef.current) {
          const container = relatedScrollRef.current;
          const needsScroll = container.scrollWidth > container.clientWidth;
          setShowRelatedArrows(needsScroll);
        }
      });
    };

    // Delay initial check to ensure DOM is ready
    const timeoutId = setTimeout(checkScrollNeeded, 0);

    // Throttle resize events to avoid excessive checks
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(checkScrollNeeded, 100);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
    };
  }, [relatedVehicles]);

  // Helper functions - defined before early returns
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  const formatMileage = (mileage: number) => {
    if (mileage === 0) return 'Zero KM';
    return new Intl.NumberFormat('pt-BR').format(mileage) + ' km';
  };

  const nextImage = () => {
    if (isImageLoading || !imagesPreloaded) return;
    setIsImageLoading(true);
    setSlideDirection('left');
    setTimeout(() => {
      setCurrentImageIndex((prev) => (prev + 1) % (vehicle?.photos?.length || 1));
      setSlideDirection(null);
      setIsImageLoading(false);
    }, 500);
  };

  const prevImage = () => {
    if (isImageLoading || !imagesPreloaded) return;
    setIsImageLoading(true);
    setSlideDirection('right');
    setTimeout(() => {
      setCurrentImageIndex((prev) => (prev - 1 + (vehicle?.photos?.length || 1)) % (vehicle?.photos?.length || 1));
      setSlideDirection(null);
      setIsImageLoading(false);
    }, 500);
  };

  const nextFullscreenImage = () => {
    if (isImageLoading) return;
    nextImage();
  };

  const prevFullscreenImage = () => {
    if (isImageLoading) return;
    prevImage();
  };

  // Show loading state
  if (isLoading || !hasLoaded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          {/* <p className="text-muted-foreground">Carregando...</p> */}
        </div>
      </div>
    );
  }

  // Show not found only after loading is complete
  if (!vehicle && hasLoaded) {
    return (
      <VehicleNotFound
        config={config}
        buttonColor={buttonColor}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
        titleColor={titleColor}
        textColor={textColor}
      />
    );
  }

  // Touch handlers for swipe navigation
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY
    });
  };

  const onTouchMove = (e: React.TouchEvent) => {
    const currentTouch = {
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY
    };
    setTouchEnd(currentTouch);
    
    // Prevent default scroll only if horizontal swipe is detected
    if (touchStart !== null) {
      const diffX = Math.abs(touchStart.x - currentTouch.x);
      const diffY = Math.abs(touchStart.y - currentTouch.y);
      
      // Only prevent if horizontal movement is greater than vertical (indicates horizontal swipe)
      if (diffX > diffY && diffX > 10) {
        e.preventDefault();
      }
    }
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distanceX = touchStart.x - touchEnd.x;
    const distanceY = Math.abs(touchStart.y - touchEnd.y);
    
    // Only trigger swipe if horizontal movement is greater than vertical
    if (Math.abs(distanceX) > distanceY) {
      const isLeftSwipe = distanceX > minSwipeDistance;
      const isRightSwipe = distanceX < -minSwipeDistance;

      if (isLeftSwipe) {
        nextImage();
      }
      if (isRightSwipe) {
        prevImage();
      }
    }
  };

  // Handle main image load complete - no longer needed (handled by preload)

  const toggleFavorite = async () => {
    const favorites = await secureGetItem('favorites') || [];
    let updatedFavorites = Array.isArray(favorites) ? [...favorites] : [];

    if (updatedFavorites.includes(vehicle.id)) {
      // Remove from favorites
      updatedFavorites = updatedFavorites.filter((id: number) => id !== vehicle.id);
      setIsFavorited(false);
    } else {
      // Add to favorites
      updatedFavorites.push(vehicle.id);
      setIsFavorited(true);

      // Track add to favorites
      trackAddToFavorites({
        id: vehicle.id,
        brand: vehicle.brand,
        model: vehicle.model,
        version: vehicle.version || '',
        year: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
        price: vehicle.price,
        mileage: vehicle.mileage
      });
    }

    await secureSetItem('favorites', updatedFavorites);

    // Dispatch custom event to update other components
    window.dispatchEvent(new Event('favoritesUpdated'));
  };

  const handleWhatsApp = () => {
    const adLead = vehicleStore.ad?.lead;

    // Check if lead type is "message" and has custom template
    if (adLead?.type === 'message' && adLead?.ios) {
      let message = adLead?.ios;
      message = message.substr(message.indexOf('&text=') + 6); // Extract message after 'text='

      // Replace dynamic placeholders based on replace array
      if (adLead.replace && Array.isArray(adLead.replace)) {
        adLead.replace.forEach((replaceItem: any) => {
          Object.keys(replaceItem).forEach((placeholder) => {
            const field = replaceItem[placeholder];
            let value = '';

            // Map field names to vehicle properties
            switch (field) {
              case 'vehicle':
                value = `${vehicle.brand} ${vehicle.model} ${vehicle.version} ${vehicle.modelYear}`;
                break;
              case 'price':
                value = formatPrice(vehicle.price);
                break;
              case 'brand':
                value = vehicle.brand;
                break;
              case 'model':
                value = vehicle.model;
                break;
              case 'version':
                value = vehicle.version;
                break;
              case 'year':
              case 'modelYear':
                value = vehicle.modelYear.toString();
                break;
              case 'mileage':
                value = `${vehicle.mileage.toLocaleString('pt-BR')} km`;
                break;
              case 'color':
                value = vehicle.color;
                break;
              case 'fuel':
                value = vehicle.fuel;
                break;
              case 'transmission':
                value = vehicle.transmission;
                break;
              default:
                // Try to get value directly from vehicle object
                value = vehicle[field] || '';
            }

            // Replace placeholder in message (case insensitive)
            const regex = new RegExp(`\\{${placeholder}\\}`, 'gi');
            message = message.replace(regex, value);
          });
        });
      }

      // Extract phone number from whatsapp?.ios URL
      const phoneMatch = vehicleStore.ad?.lead?.ios?.match(/phone=([^&]+)/);
      const phone = phoneMatch ? phoneMatch[1] : '';

      // Build WhatsApp URL
      const url = `whatsapp://send?phone=${phone}&text=${encodeURIComponent(message)}`;

      // Track WhatsApp click
      trackWhatsAppClick('vehicle_detail', {
        id: vehicle.id,
        brand: vehicle.brand,
        model: vehicle.model,
        version: vehicle.version || '',
        year: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
        price: vehicle.price,
        mileage: vehicle.mileage
      });

      // Open WhatsApp with fallback to web version
      openWhatsApp(url);
    } else {
      // Default behavior (fallback)
      const message = `Olá,\n\nTenho interesse no veículo abaixo e gostaria de receber mais informações.\n\n${vehicle.brand} ${vehicle.model} ${vehicle.version}, ${formatPrice(vehicle.price)}\n\nFico no aguardo,`;
      const url = `${vehicleStore.ad?.lead?.ios}&text=${encodeURIComponent(message)}`;

      // Track WhatsApp click
      trackWhatsAppClick('vehicle_detail', {
        id: vehicle.id,
        brand: vehicle.brand,
        model: vehicle.model,
        version: vehicle.version || '',
        year: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
        price: vehicle.price,
        mileage: vehicle.mileage
      });

      // Open WhatsApp with fallback to web version
      openWhatsApp(url);
    }
  };

  // Find financing service from vehicle's store
  const financingService = vehicleStore?.services?.find(
    (s: any) => s.service === '65dcb7898eca108d87c91dbc'
  );

  // Find test-drive service from vehicle's store
  const testDriveService = vehicleStore?.services?.find(
    (s: any) => s.service === '65dcb5e78eca108d87c91db8'
  );

  // Check if test-drive should be shown
  const showTestDrive = vehicleStore?.ad?.showTestDriveOption && testDriveService;

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${vehicle.brand} ${vehicle.model} ${vehicle.version}`,
          text: `Confira este ${vehicle.brand} ${vehicle.model} por ${formatPrice(vehicle.price)}`,
          url: window.location.href,
        });
      } catch (err) { }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      toast({
        title: '✅ Link copiado',
        description: 'O link do veículo foi copiado para a área de transferência.',
      });
    }
  };

  const openFullscreen = (index: number) => {
    if (isMobile) {
      // Use mobile-native viewer with pinch zoom
      setMobileViewerIndex(index);
      setIsMobileViewerOpen(true);
    } else {
      // Use desktop fullscreen viewer
      setFullscreenImageIndex(index);
      setIsFullscreen(true);
    }
  };

  const closeFullscreen = () => {
    setIsFullscreen(false);
  };

  const shareUrl = `${window.location.origin}${location.pathname}${location.search}`;
  const canonicalVehicleUrl = `${window.location.origin}${location.pathname}`;
  const seoVehicleTitle = `${vehicle.brand} ${vehicle.model}${vehicle.version ? ` ${vehicle.version}` : ''} ${vehicle.modelYear}`;

  return (
    <div className="min-h-screen">
      <SEOHead
        title={seoVehicleTitle}
        description={`${vehicle.brand} ${vehicle.model}${vehicle.version ? ` ${vehicle.version}` : ''} ${vehicle.modelYear} — ${vehicle.color} — ${vehicle.manufactureYear}/${vehicle.modelYear} — ${vehicle.fuel} — ${vehicle.transmission} — ${vehicle.mileage.toLocaleString('pt-BR')} km — ${formatPrice(vehicle.price)}`}
        keywords={`${vehicle.brand}, ${vehicle.model}, ${vehicle.modelYear}, ${vehicle.fuel}, ${vehicle.transmission}, ${vehicle.color}, veículo usado, carro seminovo`}
        image={vehicle.photos?.[0]}
        imageAlt={seoVehicleTitle}
        type="product"
        url={shareUrl}
        canonicalUrl={canonicalVehicleUrl}
        structuredData={generateVehicleStructuredData({
          vehicle,
          config,
          store: vehicleStore,
          vehiclePageUrl: shareUrl
        })}
      />
      
      {/* H1 Tag for SEO */}
      <h1 className="sr-only">
        {vehicle.brand} {vehicle.model}{vehicle.version ? ` ${vehicle.version}` : ''} {vehicle.modelYear} - {formatPrice(vehicle.price)}
      </h1>
      
      <Header config={config} />

      {/* Image Gallery - Full Width at Top */}
      <section className="py-0 mt-[95px] md:mt-[105px] relative z-10">
        <div className="w-full">
          <div className="relative">
            {/* Mobile View - Single Image Full Width */}
            <div className="md:hidden relative overflow-hidden">
              {/* Loading Placeholder */}
              {!imagesPreloaded && (
                <div className="aspect-[4/3] bg-muted/30 animate-pulse relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-black/10 to-transparent animate-shimmer"></div>
                </div>
              )}

              {imagesPreloaded && (
                <>
                  {/* LQIP: Blur placeholder + Full image - Mobile */}
                  <button
                    onClick={() => openFullscreen(currentImageIndex)}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                    className="relative w-full aspect-[4/3] cursor-zoom-in"
                    style={{ touchAction: 'pan-y' }}
                  >
                    {/* Low Quality Placeholder with blur */}
                    {!loadedImages.has(currentImageIndex) && !failedImages.has(currentImageIndex) && (
                      <img
                        src={vehicle.photos[currentImageIndex]}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover"
                        style={{ filter: 'blur(20px)', transform: 'scale(1.1)' }}
                        aria-hidden="true"
                        onError={(e) => {
                          // Silently handle error - don't show broken image
                          e.currentTarget.style.display = 'none';
                          setFailedImages(prev => new Set(prev).add(currentImageIndex));
                        }}
                      />
                    )}

                    {/* Full Quality Image - Optimized */}
                    {!failedImages.has(currentImageIndex) ? (() => {
                      // Mobile: full width, aspect ratio 4:3
                      const mobileOptimized = generateResponsiveImageUrls(
                        vehicle.photos[currentImageIndex],
                        400, // Mobile width
                        300  // Mobile height (4:3 aspect)
                      );
                      const mobileStrategy = getImageLoadingStrategy(true, true);
                      
                      return (
                        <img
                          src={mobileOptimized.src}
                          srcSet={mobileOptimized.srcSet}
                          sizes={mobileOptimized.sizes || '(max-width: 640px) 100vw, 400px'}
                          width={mobileOptimized.width}
                          height={mobileOptimized.height}
                          alt={`${vehicle.brand} ${vehicle.model}`}
                          className={`relative w-full h-full object-cover transition-opacity duration-500 ${loadedImages.has(currentImageIndex) ? 'opacity-100' : 'opacity-0'
                            }`}
                          loading={mobileStrategy.loading}
                          fetchpriority={mobileStrategy.fetchpriority}
                          decoding="async"
                          onLoad={() => {
                            setLoadedImages(prev => new Set(prev).add(currentImageIndex));
                          }}
                          onError={(e) => {
                            // Silently handle error - show placeholder instead
                            e.currentTarget.style.display = 'none';
                            setFailedImages(prev => new Set(prev).add(currentImageIndex));
                          }}
                        />
                      );
                    })() : (
                      <div className="relative w-full h-full bg-muted/30 flex items-center justify-center">
                        <div className="text-center text-muted-foreground">
                          <Car className="h-16 w-16 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">Imagem não disponível</p>
                        </div>
                      </div>
                    )}
                  </button>

                  {/* Top Badges - Mobile */}
                  <div className="absolute top-6 left-4 right-4 flex items-start justify-between z-20 pointer-events-none">
                    {/* Badges */}
                    <div className="flex flex-col gap-2 pointer-events-auto">
                      {vehicle.isZeroKm && (
                        <Badge className="bg-green-600/95 backdrop-blur-md text-white text-sm font-semibold px-3 py-1.5 shadow-lg">
                          Zero KM
                        </Badge>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2 pointer-events-auto">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite();
                        }}
                        className="p-2 bg-white/95 backdrop-blur-md rounded-full hover:bg-white transition-all shadow-lg hover:scale-110"
                      >
                        <Heart className={`h-4 w-4 transition-all ${isFavorited ? 'fill-red-500 text-red-500' : 'text-gray-600'}`} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShare();
                        }}
                        className="p-2 bg-white/95 backdrop-blur-md rounded-full hover:bg-white transition-all shadow-lg hover:scale-110"
                      >
                        <Share2 className="h-4 w-4 text-gray-600" />
                      </button>
                    </div>
                  </div>

                  {/* Navigation Arrows - Removed (use swipe gestures) */}

                  {/* Counter - Mobile */}
                  {vehicle.photos.length > 1 && (
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1 z-20">
                      {vehicle.photos.map((_, idx) => (
                        <div
                          key={idx}
                          className={`h-1 rounded-full transition-all ${idx === currentImageIndex ? 'w-6 bg-white' : 'w-1.5 bg-white/50'
                            }`}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Desktop View - 3 Images Side by Side */}
            <div className="hidden md:block relative overflow-hidden h-[280px] lg:h-[320px] xl:h-[340px]">
              {/* Loading Placeholder - 3 Images */}
              {!imagesPreloaded && (
                <div className="h-[280px] lg:h-[320px] xl:h-[340px] flex gap-0">
                  {/* Left placeholder */}
                  <div className="w-1/3 h-full bg-muted/20 animate-pulse relative overflow-hidden opacity-60">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-black/10 to-transparent animate-shimmer"></div>
                  </div>
                  {/* Center placeholder */}
                  <div className="w-1/3 h-full bg-muted/30 animate-pulse relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-black/10 to-transparent animate-shimmer" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                  {/* Right placeholder */}
                  <div className="w-1/3 h-full bg-muted/20 animate-pulse relative overflow-hidden opacity-60">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-black/10 to-transparent animate-shimmer" style={{ animationDelay: '0.4s' }}></div>
                  </div>
                </div>
              )}

              {/* Wrapper with transform for slide effect */}
              {imagesPreloaded && (() => {
                // Create 3 copies: [copy1, copy2, copy3]
                // We always stay in copy2 (middle), showing 3 images at a time
                const allImages = [...vehicle.photos, ...vehicle.photos, ...vehicle.photos];
                const photosLength = vehicle.photos.length;

                // Center position is in the middle copy (index photosLength + currentImageIndex)
                const centerIndex = photosLength + currentImageIndex;

                // Each slide = 1/n of track; track shows 3 slides in viewport (100% wide)
                const slideCount = allImages.length;
                const slideStep = 100 / slideCount;
                const translateBase = (centerIndex - 1) * slideStep;
                const slideOffset = slideDirection === 'left' ? -slideStep : slideDirection === 'right' ? slideStep : 0;

                return (
                  <div
                    className="flex gap-0 h-full will-change-transform"
                    style={{
                      width: `${(slideCount / 3) * 100}%`,
                      transform: `translateX(calc(-${translateBase}% + ${slideOffset}%))`,
                      transition: slideDirection ? 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none',
                    }}
                  >
                    {allImages.map((photo: string, index: number) => {
                      const actualIndex = index % photosLength;
                      const isCenterImage = index === centerIndex;
                      const isAdjacentImage = Math.abs(index - centerIndex) === 1;

                      return (
                        <div
                          key={`vehicle-img-${index}`}
                          className={`relative overflow-hidden flex-shrink-0 h-full ${isCenterImage
                            ? 'z-10'
                            : 'opacity-60 hover:opacity-80'
                            }`}
                          style={{
                            width: `${slideStep}%`,
                          }}
                        >
                          <button
                            onClick={() => {
                              if (isImageLoading) return;

                              // If center image, open fullscreen
                              if (isCenterImage) {
                                openFullscreen(currentImageIndex);
                                return;
                              }

                              // Only allow clicking adjacent images
                              if (isAdjacentImage) {
                                if (index > centerIndex) {
                                  nextImage();
                                } else {
                                  prevImage();
                                }
                              }
                            }}
                            className={`absolute inset-0 ${isCenterImage ? 'cursor-zoom-in' : isAdjacentImage ? 'cursor-pointer' : 'cursor-default'}`}
                          >
                            {/* Loading Placeholder - Shimmer effect */}
                            {isImageLoading && isCenterImage && (
                              <div className="absolute inset-0 bg-muted/20 animate-pulse z-20 overflow-hidden">
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                              </div>
                            )}

                            {/* LQIP: Blur placeholder + Full image */}
                            {!loadedImages.has(actualIndex) && !failedImages.has(actualIndex) && (
                              <img
                                src={photo}
                                alt=""
                                className="absolute inset-0 w-full h-full object-cover"
                                style={{ filter: 'blur(20px)', transform: 'scale(1.1)' }}
                                aria-hidden="true"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                  setFailedImages(prev => new Set(prev).add(actualIndex));
                                }}
                              />
                            )}

                            {/* Full Quality Image - Optimized */}
                            {!failedImages.has(actualIndex) ? (() => {
                              const desktopOptimized = generateResponsiveImageUrls(
                                photo,
                                640,
                                480
                              );
                              const desktopStrategy = getImageLoadingStrategy(
                                isCenterImage && actualIndex === currentImageIndex,
                                isCenterImage && actualIndex === currentImageIndex
                              );
                              
                              return (
                                <img
                                  src={desktopOptimized.src}
                                  srcSet={desktopOptimized.srcSet}
                                  sizes="33vw"
                                  alt={`${vehicle.brand} ${vehicle.model} - Foto ${actualIndex + 1}`}
                                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${loadedImages.has(actualIndex) ? 'opacity-100' : 'opacity-0'
                                    }`}
                                  loading={desktopStrategy.loading}
                                  fetchpriority={desktopStrategy.fetchpriority}
                                  decoding="async"
                                  onLoad={() => {
                                    setLoadedImages(prev => new Set(prev).add(actualIndex));
                                  }}
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                    setFailedImages(prev => new Set(prev).add(actualIndex));
                                  }}
                                />
                              );
                            })() : (
                              <div className="absolute inset-0 bg-muted/30 flex items-center justify-center">
                                <div className="text-center text-muted-foreground">
                                  <Car className="h-12 w-12 mx-auto mb-2 opacity-50" />
                                  <p className="text-xs">Imagem não disponível</p>
                                </div>
                              </div>
                            )}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Navigation Arrows - Removed (use swipe on mobile or thumbnails on desktop) */}

              {/* Top Badges and Actions */}
              {imagesPreloaded && (
                <div className="absolute top-7 md:top-8 left-4 right-4 flex items-start justify-between z-20 pointer-events-none">
                  {/* Badges */}
                  <div className="flex flex-col gap-2 pointer-events-auto">
                    {vehicle.isZeroKm && (
                      <Badge className="bg-green-600/95 backdrop-blur-md text-white text-sm font-semibold px-3 py-1.5 shadow-lg">
                        Zero KM
                      </Badge>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 pointer-events-auto">
                    <button
                      onClick={toggleFavorite}
                      className="p-2.5 bg-white/95 backdrop-blur-md rounded-full hover:bg-white transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-110"
                      title={isFavorited ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
                    >
                      <Heart
                        className={`h-5 w-5 transition-all ${isFavorited ? 'fill-red-500 text-red-500' : 'text-gray-600'}`}
                      />
                    </button>
                    <button
                      onClick={handleShare}
                      className="p-2.5 bg-white/95 backdrop-blur-md rounded-full hover:bg-white transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-110"
                      title="Compartilhar"
                    >
                      <Share2 className="h-5 w-5 text-gray-600" />
                    </button>
                    <button
                      onClick={() => !isImageLoading && openFullscreen(currentImageIndex)}
                      disabled={isImageLoading}
                      className="p-2.5 bg-white/95 backdrop-blur-md rounded-full hover:bg-white transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-110"
                      title="Tela cheia"
                    >
                      <Maximize className="h-5 w-5 text-gray-600" />
                    </button>
                  </div>
                </div>
              )}

              {/* Bottom Counter */}
              {imagesPreloaded && vehicle.photos.length > 1 && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1 z-20">
                  {vehicle.photos.map((_, idx) => (
                    <div
                      key={idx}
                      className={`h-1.5 rounded-full transition-all ${idx === currentImageIndex ? 'w-8 bg-white' : 'w-2 bg-white/50'
                        }`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Section */}

      <section className="py-8 relative z-30">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Vehicle Details */}
            <div className="lg:col-span-2 space-y-6">
              {/* Main Vehicle Info Card - Clean & Professional */}
              <Card>
                <CardContent className="p-0">
                  {/* Title Section - Clean Header */}
                  <div className="py-6 border-b mx-8">
                    <h1 className="text-2xl lg:text-3xl font-bold" style={{ color: titleColor }}>
                      {vehicle.brand} {vehicle.model}
                    </h1>
                    <p className="text text-muted-foreground font-medium">
                      {vehicle.version}
                    </p>
                  </div>

                  {/* Specifications Grid - Clean Minimal */}
                  <div className="p-8 pt-6">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                      {/* Ano - Always show */}
                      {(vehicle.manufactureYear || vehicle.modelYear) && (
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5">
                            <Calendar className="h-5 w-5 text-muted-foreground" />
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground mb-1">Ano</p>
                            <p className="text-base font-semibold text-foreground">{vehicle.manufactureYear}/{vehicle.modelYear}</p>
                          </div>
                        </div>
                      )}

                      {/* KM - Always show */}
                      {vehicle.mileage !== undefined && vehicle.mileage !== null && (
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5">
                            <Gauge className="h-5 w-5 text-muted-foreground" />
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground mb-1">KM</p>
                            <p className="text-base font-semibold text-foreground">{formatMileage(vehicle.mileage)}</p>
                          </div>
                        </div>
                      )}

                      {/* Fuel - Only if exists */}
                      {vehicle.fuel && (
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5">
                            <Fuel className="h-5 w-5 text-muted-foreground" />
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground mb-1">Combustível</p>
                            <p className="text-base font-semibold text-foreground">{vehicle.fuel}</p>
                          </div>
                        </div>
                      )}

                      {/* Transmission - Only for Carro and Caminhão, and if exists */}
                      {vehicle.type !== 2 && vehicle.transmission && (
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5">
                            <Settings className="h-5 w-5 text-muted-foreground" />
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground mb-1">Câmbio</p>
                            <p className="text-base font-semibold text-foreground">{vehicle.transmission}</p>
                          </div>
                        </div>
                      )}

                      {/* Color - Only if exists */}
                      {vehicle.color && (
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5">
                            <Palette className="h-5 w-5 text-muted-foreground" />
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground mb-1">Cor</p>
                            <p className="text-base font-semibold text-foreground">{vehicle.color}</p>
                          </div>
                        </div>
                      )}

                      {/* Doors - Only for Carro and Caminhão, and if exists */}
                      {vehicle.type !== 2 && vehicle.doors && (
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5">
                            <Car className="h-5 w-5 text-muted-foreground" />
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground mb-1">Portas</p>
                            <p className="text-base font-semibold text-foreground">{vehicle.doors}</p>
                          </div>
                        </div>
                      )}

                      {/* Store - Only show if multiple stores */}
                      {availableStores.length > 1 && vehicle.store && (() => {
                        const vehicleStore = config.stores?.find((s: any) => s._id === vehicle.store);
                        return vehicleStore ? (
                          <div className="flex items-start gap-3">
                            <div className="mt-0.5">
                              <Building2 className="h-5 w-5 text-muted-foreground" />
                            </div>
                            <div>
                              <p className="text-sm text-muted-foreground mb-1">Loja</p>
                              <p className="text-base font-semibold text-foreground">{vehicleStore.company}</p>
                            </div>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  </div>

                  {/* Statistics - Elegant Badges */}
                  {vehicle.statistics && (vehicle.statistics.financing || vehicle.statistics.favorites) && (
                    <div className="px-8 pb-8">
                      <div className="pt-8 border-t">
                        <div className="flex flex-wrap gap-3">
                          {vehicle.statistics.financing && config.stores?.[0]?.ad?.displayNumberOfFinancingProposals && (
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-muted/50 text-sm">
                              <FilePen className="h-4 w-4 text-muted-foreground" />
                              <span className="text-muted-foreground">{vehicle.statistics.financing > 1 ? `${vehicle.statistics.financing} propostas em análise` : `${vehicle.statistics.financing} proposta em análise`}</span>
                            </div>
                          )}

                          {vehicle.statistics.favorites && vehicleStore?.ad?.displayNumberOfInterestedCustomers && (
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-muted/50 text-sm">
                              <Heart className="h-4 w-4 text-muted-foreground" />
                              <span className="text-muted-foreground">{vehicle.statistics.favorites > 1 ? `${vehicle.statistics.favorites} clientes interessados` : `${vehicle.statistics.favorites} cliente interessado`}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Vehicle Description - Clean & Elegant */}
                  {vehicle.description && (
                    <p className="text-muted-foreground leading-relaxed border-t py-8 mx-8">
                      {vehicle.description}
                    </p>
                  )}

                  {/* Optionals - Clean Professional Grid */}
                  {vehicle.optionals && vehicle.optionals.length > 0 && (
                    <div className='border-t mx-8'>
                      <div className='p-0 m-0'>
                        <CardHeader className='p-0 pt-6 m-0'>
                          <div className="flex items-center">
                            <CardTitle className="text-xl font-bold flex items-center gap-2">
                              <div className="w-1 h-6 rounded-full" style={{ backgroundColor: primaryColor }} />
                              Opcionais
                            </CardTitle>
                            <Badge variant="secondary" className="text-sm font-semibold ml-4">
                              {vehicle.optionals.length}
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent className='p-0 pb-6 pt-4 m-0'>
                          <div>
                            {vehicle.optionals.map((optional: string, index: number) => (
                              <Badge key={index} variant="secondary" className="text-sm font-normal m-1" style={{ background: primaryColor, opacity: 0.8 }}>
                                <span className="text-foreground text-white">{optional}</span>
                              </Badge>
                            ))}
                          </div>
                        </CardContent>
                      </div>
                    </div>
                  )}

                  {/* Warning */}
                  {
                    config.stores?.[0]?.ad?.warning && (
                      <div className="p-8">
                        <Card className="border-yellow-200 bg-yellow-50/50">
                          <CardContent className="p-6">
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0 mt-0.5">
                                <svg className="h-5 w-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div>
                                <h3 className="font-semibold text-yellow-900 mb-1">Aviso</h3>
                                <p className="text-sm text-yellow-800 leading-relaxed">
                                  {vehicleStore.ad.warning}
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    )
                  }

                </CardContent>
              </Card>

              {/* Financing Card - Inline Form (when financing service exists with type message) */}
              {financingService && financingService.lead?.type === 'message' && (
                <Card className="mt-6 border-primary/20">
                  <CardContent className="p-6">
                    <Suspense fallback={null}>
                      <ServiceModal
                        service={financingService}
                        serviceTitle="Simular Financiamento"
                        config={config}
                        renderInline={true}
                        preSelectedVehicle={vehicle}
                        serviceImage={financing}
                      />
                    </Suspense>
                  </CardContent>
                </Card>
              )}

              {/* Financing Card - Inline Form (when financing service exists with type api) */}
              {financingService && (financingService.lead?.type === 'api' || financingService.lead?.type === 'email') && (
                <Card className="mt-6  border-primary/20">
                  <CardContent className="p-6">
                    <LeadForm
                      vehicle={vehicle}
                      config={config}
                      buttonColor={buttonColor}
                      store={vehicleStore}
                      serviceType="65dcb7898eca108d87c91dbc"
                      serviceTitle="Simular Financiamento"
                      service={financingService}
                    />
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Right Column - Vehicle Info & Contact */}
            <div className="space-y-6">
              {/* Main Info Card - Clean Professional */}
              <Card>
                <CardContent className="p-0">
                  {/* Price Section - Elegant */}
                  <div className="p-8 border-b">
                    <div className="text-4xl font-bold mb-1" style={{ color: titleColor }}>
                      {formatPrice(vehicle.price)}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="p-6 space-y-3">
                    {/* Lead Section - Form for API, Button for Message */}
                    {(vehicleStore.ad?.lead?.type === 'api' || vehicleStore.ad?.lead?.type === 'email') ? (
                      <LeadForm
                        vehicle={vehicle}
                        config={config}
                        buttonColor={buttonColor}
                        store={vehicleStore}
                        serviceType="message"
                        serviceTitle="Envie uma mensagem"
                        service={{ lead: vehicleStore.ad.lead }} />
                    ) : (
                      <Button
                        className="w-full text-white transition-all duration-200"
                        style={{
                          backgroundColor: buttonColor,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.opacity = '0.9';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.opacity = '1';
                        }}
                        onClick={handleWhatsApp}
                        size="lg"
                      >
                        Enviar mensagem
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Guarantee Badge - Clean Professional */}
              <Card style={{ borderColor: secondaryColor }}>
                <CardContent className="p-6 text-center">
                  <div className="inline-flex p-3 rounded-full mb-3" style={{ backgroundColor: `${primaryColor}15` }}>
                    <Shield className="h-7 w-7" style={{ color: primaryColor }} />
                  </div>
                  <h3 className="font-bold mb-2">Garantia e Qualidade</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Todos os nossos veículos passam por criteriosa inspeção antes da entrega.
                  </p>
                </CardContent>
              </Card>

              {/* Test-Drive Card */}
              {showTestDrive && (
                <Card>
                  <CardContent className="p-6">
                    <div className="mb-4">
                      <h3 className="text-lg font-bold mb-2">Test-Drive</h3>
                      <p className="text-sm text-muted-foreground">
                        Agende um test drive e experimente o veículo dos seus sonhos
                      </p>
                    </div>
                    <Suspense fallback={null}>
                      <ServiceModal
                        service={testDriveService}
                        config={config}
                        preSelectedVehicle={vehicle}
                        serviceImage={testdrive}
                      >
                        <Button
                          className="w-full"
                          variant="outline"
                          size="lg"
                        >
                          <Gauge className="h-5 w-5 mr-2" />
                          Agendar Test-Drive
                        </Button>
                      </ServiceModal>
                    </Suspense>
                  </CardContent>
                </Card>
              )}

              {/* Store Information */}
              {vehicleStore?.company && (
                <Card>
                  <CardContent className="p-6">
                    <div className="space-y-4">
                      {availableStores.length > 1 &&
                        <div>
                          <p className="font-semibold text-lg">{vehicleStore.company}</p>
                        </div>
                      }

                      {availableStores.length > 1 && vehicleStore.place?.address && (
                        <div className="flex items-start gap-2 text-sm">
                          <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                          <span className="text-muted-foreground">{vehicleStore.place.address}</span>
                        </div>
                      )}

                      {availableStores.length > 1 && userLocation && (() => {
                        const distance = getDistanceToStore(userLocation, vehicleStore);
                        return distance !== null ? (
                          <div className="flex items-center gap-2">
                            <Navigation className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">{formatDistance(distance)}</span>
                          </div>
                        ) : null;
                      })()}

                      {availableStores.length > 1 && <Separator className="my-4" />}

                      {/* Contact Buttons */}
                      <div className="space-y-2">
                        {/* WhatsApp Button */}
                        <Button
                          onClick={() => {
                            if (vehicleStore?.whatsapp?.ios) {
                              openWhatsApp(vehicleStore.whatsapp?.ios);
                            }
                          }}
                          className="w-full bg-green-600 hover:bg-green-700 text-white transition-all duration-200"
                          size="default"
                        >
                          <MessageCircle className="h-4 w-4 mr-2" />
                          WhatsApp
                        </Button>

                        {/* Call Button */}
                        <Button
                          onClick={() => {
                            if (vehicleStore?.phone?.ios) {
                              window.open(vehicleStore.phone?.ios, '_blank');
                            }
                          }}
                          variant="outline"
                          className="w-full transition-all duration-200"
                          size="default"
                        >
                          <Phone className="h-4 w-4 mr-2" />
                          Ligar
                        </Button>

                        {/* Directions Button */}
                        <Button
                          variant="outline"
                          size="default"
                          className="w-full"
                          onClick={() => {
                            const place = vehicleStore?.place;
                            openStoreDirections(place);
                          }}
                        >
                          <MapPin className="h-4 w-4 mr-2" />
                          Como Chegar
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div >

          {/* Related Vehicles */}
          {vehicleStore?.ad?.showRelatedOffers && relatedVehicles.length > 0 && (
            <section className="mt-16">
              {/* Section Header - Simple and Left Aligned */}
              <div className="mb-4">
                <h2 className="text-2xl font-bold text-foreground">Veículos Relacionados</h2>
              </div>

              <div className="relative group/carousel -mx-4 px-4">
                <div
                  ref={relatedScrollRef}
                  id="related-vehicles-scroll"
                  className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide scroll-smooth pb-4"
                  style={{
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                  }}
                >
                  {/* Real Vehicles - Max 5 */}
                  {relatedVehicles.slice(0, 5).map((relatedVehicle) => (
                    <div key={relatedVehicle.id} className="flex-shrink-0 w-[260px] md:w-[260px] snap-start">
                      <VehicleCard vehicle={relatedVehicle} whatsappUrl={vehicleStore?.whatsapp?.ios} />
                    </div>
                  ))}

                  {/* Placeholders */}
                  {relatedVehicles.length < 5 && Array.from({ length: 5 - relatedVehicles.length }).map((_, index) => (
                    <div key={`placeholder-${index}`} className="flex-shrink-0 w-[260px] md:w-[260px] snap-start">
                      <Card className="overflow-hidden border-dashed border-border/50 bg-muted/20 flex flex-col h-full">
                        {/* Placeholder Image - Same aspect ratio as VehicleCard */}
                        <div className="relative aspect-[4/2.5] overflow-hidden flex-shrink-0 bg-muted/30 flex items-center justify-center">
                          <Eye className="h-8 w-8 text-muted-foreground/30" />
                        </div>

                        {/* Compact Info Section - Match VehicleCard structure */}
                        <CardContent className="p-4 flex flex-col">
                          {/* Title and Version - Fixed heights */}
                          <div className="mb-3 flex-shrink-0">
                            <div className="h-6 bg-muted/50 rounded w-3/4 mb-2"></div>
                            <div className="h-4 bg-muted/30 rounded w-1/2 mb-2"></div>
                            {/* Price Skeleton */}
                            <div className="h-7 bg-muted/50 rounded w-2/3"></div>
                          </div>

                          {/* Details - Single Row with fixed height */}
                          <div className="flex items-center gap-2 border-t border-border/50 pt-3 flex-shrink-0 h-8">
                            <div className="h-3 bg-muted/30 rounded w-16"></div>
                            <div className="w-1 h-1 bg-muted/30 rounded-full"></div>
                            <div className="h-3 bg-muted/30 rounded w-12"></div>
                          </div>

                          {/* CTA Space */}
                          <div className="h-4 mt-3 flex-shrink-0"></div>
                        </CardContent>
                      </Card>
                    </div>
                  ))}
                </div>

                {/* Navigation Arrows - Only show if overflow exists */}
                {showRelatedArrows && (
                  <>
                    <button
                      className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 bg-white/95 hover:bg-white rounded-full p-2.5 transition-all opacity-0 group-hover/carousel:opacity-100 z-10 border border-gray-200 shadow-lg"
                      onClick={() => {
                        if (relatedScrollRef.current) {
                          relatedScrollRef.current.scrollBy({
                            left: -276,
                            behavior: 'smooth'
                          });
                        }
                      }}
                      aria-label="Anterior"
                    >
                      <ChevronLeft className="h-5 w-5 text-gray-800" />
                    </button>

                    <button
                      className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 bg-white/95 hover:bg-white rounded-full p-2.5 transition-all opacity-0 group-hover/carousel:opacity-100 z-10 border border-gray-200 shadow-lg"
                      onClick={() => {
                        if (relatedScrollRef.current) {
                          relatedScrollRef.current.scrollBy({
                            left: 276,
                            behavior: 'smooth'
                          });
                        }
                      }}
                      aria-label="Próximo"
                    >
                      <ChevronRight className="h-5 w-5 text-gray-800" />
                    </button>
                  </>
                )}
              </div>
            </section>
          )}
        </div >
      </section >

      <Footer config={config} />

      {/* Fullscreen Gallery Modal */}
      {
        isFullscreen && (
          <div
            className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center"
            onClick={closeFullscreen}
          >
            {/* Close Button */}
            <button
              onClick={closeFullscreen}
              className="absolute top-4 right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
            >
              <X className="h-6 w-6 text-white" />
            </button>

            {/* Image Counter */}
            <div className="absolute top-4 left-4 bg-black/70 text-white px-4 py-2 rounded-lg text-sm z-10">
              {currentImageIndex + 1} / {vehicle.photos.length}
            </div>

            {/* Main Image with Smooth Transition - Limited height to leave space for thumbnails */}
            <div
              className="relative w-full flex items-center justify-center pt-8 pb-32 rounded-lg"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Images container - Always renders current with adjacent images for smooth slide */}
              <div className="relative h-full overflow-hidden w-[830px] rounded-lg">
                {(() => {
                  // Calculate which images to show based on slideDirection
                  const photosLength = vehicle.photos.length;
                  const prevIndex = (currentImageIndex - 1 + photosLength) % photosLength;
                  const nextIndex = (currentImageIndex + 1) % photosLength;
                  
                  // Calculate transform offset
                  const baseOffset = -100; // Center is at -100%
                  const slideOffset = slideDirection === 'left' ? -100 : slideDirection === 'right' ? 100 : 0;
                  
                  return (
                    <div 
                      className="flex h-full rounded-lg"
                      style={{
                        transform: `translateX(${baseOffset + slideOffset}%)`,
                        transition: slideDirection ? 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none'
                      }}
                    >
                      {/* Previous Image (left) */}
                      <div className="w-full flex-shrink-0 flex items-center justify-center px-0 rounded-lg">
                        {!failedImages.has(prevIndex) ? (
                          <img
                            src={vehicle.photos[prevIndex]}
                            alt=""
                            className="max-w-full max-h-full object-contain rounded-lg"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              setFailedImages(prev => new Set(prev).add(prevIndex));
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-muted/30 rounded-lg">
                            <Car className="h-16 w-16 opacity-50 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      
                      {/* Current Image (center) */}
                      <div className="w-full flex-shrink-0 flex items-center justify-center px-0 rounded-lg">
                        {!failedImages.has(currentImageIndex) ? (
                          <img
                            src={vehicle.photos[currentImageIndex]}
                            alt={`${vehicle.brand} ${vehicle.model}`}
                            className="max-w-full max-h-full object-contain rounded-lg"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              setFailedImages(prev => new Set(prev).add(currentImageIndex));
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-muted/30 rounded-lg">
                            <Car className="h-16 w-16 opacity-50 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      
                      {/* Next Image (right) */}
                      <div className="w-full flex-shrink-0 flex items-center justify-center px-0 rounded-lg">
                        {!failedImages.has(nextIndex) ? (
                          <img
                            src={vehicle.photos[nextIndex]}
                            alt=""
                            className="max-w-full max-h-full object-contain rounded-lg"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              setFailedImages(prev => new Set(prev).add(nextIndex));
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-muted/30 rounded-lg">
                            <Car className="h-16 w-16 opacity-50 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Navigation Arrows */}
              {vehicle.photos.length > 1 && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      prevFullscreenImage();
                    }}
                    disabled={isImageLoading}
                    className={`absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-4 rounded-full transition-all duration-300 shadow-xl z-40 ${
                      isImageLoading ? 'opacity-30' : 'hover:scale-110'
                    }`}
                  >
                    <ChevronLeft className="h-8 w-8" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      nextFullscreenImage();
                    }}
                    disabled={isImageLoading}
                    className={`absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-4 rounded-full transition-all duration-300 shadow-xl z-40 ${
                      isImageLoading ? 'opacity-30' : 'hover:scale-110'
                    }`}
                  >
                    <ChevronRight className="h-8 w-8" />
                  </button>
                </>
              )}
            </div>

            {/* Thumbnail Strip - Show only nearby thumbnails */}
            {vehicle.photos.length > 1 && (
              <div className="absolute bottom-0 mb-1 left-1/2 transform -translate-x-1/2 w-full px-4">
                <div className="rounded-lg p-4">
                  <div className="flex gap-2 justify-center">
                    {(() => {
                      const totalPhotos = vehicle.photos.length;
                      const maxVisible = 8;

                      // If 7 or fewer photos, show all
                      if (totalPhotos <= maxVisible) {
                        return vehicle.photos.map((photo: string, index: number) => (
                          <button
                            key={index}
                            onClick={(e) => {
                              e.stopPropagation();
                              // Update main gallery index - will sync via useEffect
                              setCurrentImageIndex(index);
                            }}
                            className={`flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 transition-all ${index === currentImageIndex
                              ? 'border-white scale-110'
                              : 'border-transparent hover:border-white/50 opacity-70 hover:opacity-100'
                              }`}
                          >
                            <img
                              src={photo}
                              alt={`Foto ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </button>
                        ));
                      }

                      // Show thumbnails around current image
                      const halfVisible = Math.floor(maxVisible / 2);
                      let startIndex = Math.max(0, currentImageIndex - halfVisible);
                      let endIndex = Math.min(totalPhotos, startIndex + maxVisible);

                      // Adjust if we're near the end
                      if (endIndex - startIndex < maxVisible) {
                        startIndex = Math.max(0, endIndex - maxVisible);
                      }

                      return vehicle.photos.slice(startIndex, endIndex).map((photo: string, index: number) => {
                        const actualIndex = startIndex + index;
                        return (
                          <button
                            key={actualIndex}
                            onClick={(e) => {
                              e.stopPropagation();
                              // Update main gallery index - will sync via useEffect
                              setCurrentImageIndex(actualIndex);
                            }}
                            className={`flex-shrink-0 w-28 h-16 rounded-lg overflow-hidden border-2 transition-all ${actualIndex === currentImageIndex
                              ? 'border-white scale-100'
                              : 'border-transparent hover:border-white/50 opacity-70 hover:opacity-100'
                              }`}
                          >
                            <img
                              src={photo}
                              alt={`Foto ${actualIndex + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </button>
                        );
                      });
                    })()}
                  </div>
                </div>
              </div>
            )}

            {/* Instructions - Hidden on mobile */}
            {/* <div className="hidden md:block absolute bottom-16 mb-6 left-1/2 transform -translate-x-1/2 text-white/60 text-sm">
              Use as setas ← → do teclado ou clique nas setas para navegar | ESC para fechar
            </div> */}
          </div>
        )
      }

      {/* Mobile Image Viewer with Pinch Zoom */}
      {vehicle && (
        <MobileImageViewer
          images={vehicle.photos}
          initialIndex={mobileViewerIndex}
          isOpen={isMobileViewerOpen}
          onClose={() => setIsMobileViewerOpen(false)}
          onImageChange={(index) => setCurrentImageIndex(index)}
          vehicleTitle={`${vehicle.brand} ${vehicle.model} ${vehicle.version}`}
        />
      )}

    </div >
  );
};

export default VehicleDetail;